import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "../../axios-instance";
import setAuthToken from "../../utils/setAuthToken";
import { setCurrentUser } from "../../actions/authActions";
import { fetchUserFeatures } from "../../actions/featureAccessActions";
import { SET_IMPERSONATION_ACTIVE } from "../../actions/types";
import { FEATURES_FETCH_SUCCESS } from "../../constants/featureAccess";
import { notification } from "../../utils/utility";
import { calendarSoftFailMessage } from "../../utils/socialAuth";
import {
  redirectIntoApp,
  shouldSkipFeatureFetchOnLogin,
} from "../../utils/authHelper";
import Loader from "../../components/Loader/Loader";

/**
 * Landing page after Google/Microsoft OAuth.
 * Exchanges OTC for Passport token, or routes to 2FA / error.
 * New social signups go to the payment page (skip email/password profile steps).
 */
const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    const error = searchParams.get("error");
    const challengeToken = searchParams.get("challenge_token");
    const code = searchParams.get("code");
    const isNew = searchParams.get("is_new") === "1";
    const calendarConnected = searchParams.get("calendar_connected") === "1";
    const calendarReason = searchParams.get("calendar_reason");

    if (error) {
      // Do NOT wipe impersonator_session on error — SA can still exit impersonation.
      setBusy(false);
      const messages = {
        access_denied: "Sign-in was cancelled.",
        oauth_denied: "Sign-in was cancelled or denied.",
        no_account: "No account found. Please sign up first.",
        staff_invite_required:
          "Please complete your staff invitation link instead of signing up here.",
        email_required: "Email permission is required to continue.",
        email_unverified: "Please use a verified email with Google or Microsoft.",
        account_deactivated: "This account is deactivated.",
        account_deleted: "This account is no longer available.",
        super_admin_blocked: "This account cannot use social sign-in.",
        provider_conflict: "This Google/Microsoft account is already linked to another user.",
        state_invalid: "Sign-in session expired. Please try again.",
        oauth_failed: "Authentication failed. Please try again.",
        oauth_config_error: "Social sign-in is not configured. Please contact support.",
        signup_failed: "Could not create your account. Please try again.",
        invalid_provider: "Unsupported sign-in provider.",
      };
      notification.error(messages[error] || "Sign-in failed. Please try again.");
      navigate(error === "no_account" ? "/signup" : "/admin/login");
      return;
    }

    if (challengeToken) {
      // Do not clear impersonator_session here — 2FA may be abandoned.
      // Google2FA success / OTC success paths clear it when a real session is issued.
      navigate(`/verify-pin/${encodeURIComponent(challengeToken)}`);
      return;
    }

    if (!code) {
      setBusy(false);
      notification.error("Sign-in session expired. Please try again.");
      navigate("/admin/login");
      return;
    }

    axios
      .post("auth/oauth/exchange", { code })
      .then((res) => {
        const data = res.data.data || {};
        const { access_token, jwt_custom_claims } = data;
        if (!access_token || !jwt_custom_claims || !jwt_custom_claims.user) {
          throw new Error("Invalid token response");
        }

        // Successful social session replaces any impersonation snapshot.
        localStorage.removeItem("impersonator_session");
        dispatch({ type: SET_IMPERSONATION_ACTIVE, payload: false });

        localStorage.removeItem("userType");
        localStorage.removeItem("businessId");
        localStorage.setItem("config", JSON.stringify(jwt_custom_claims));
        localStorage.setItem("userTimezone", jwt_custom_claims.timezone);
        localStorage.setItem("userTimezoneId", jwt_custom_claims.timezone_id);
        localStorage.setItem("page", jwt_custom_claims.page);
        localStorage.setItem("jwtToken", access_token);
        setAuthToken(access_token);

        dispatch(setCurrentUser(jwt_custom_claims.user));

        const connected =
          typeof data.calendar_connected === "boolean"
            ? data.calendar_connected
            : calendarConnected;
        const reason = data.calendar_reason || calendarReason;
        const newUser = isNew || data.is_new;

        const afterFeatures = () => {
          if (newUser) {
            const businessIds = Object.values(jwt_custom_claims.businesses || {});
            if (businessIds.length > 0) {
              localStorage.setItem("businessId", businessIds[businessIds.length - 1]);
            }
            if (!connected) {
              notification.warning(calendarSoftFailMessage(reason));
            } else {
              notification.success("Signed up successfully");
            }
            // Social signup: skip the profile wizard, then pick a plan (trial).
            navigate("/signup/payment");
            return;
          }

          notification.success("Logged in successfully");
          redirectIntoApp(navigate);
        };

        if (!shouldSkipFeatureFetchOnLogin()) {
          // Wait for features before admin dashboard (parity with Login.js)
          axios
            .get("user-plan-features")
            .then((featRes) => {
              dispatch({
                type: FEATURES_FETCH_SUCCESS,
                payload: featRes.data.data,
              });
            })
            .catch(() => {
              dispatch(fetchUserFeatures());
            })
            .finally(afterFeatures);
        } else {
          afterFeatures();
        }
      })
      .catch(() => {
        setBusy(false);
        notification.error("Sign-in session expired. Please try again.");
        navigate("/admin/login");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <Loader isShowLoader={busy} />;
};

export default OAuthCallback;
